import { useState } from "react"
import { Link, useParams, useNavigate } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button, Card, Input, Label, Dialog, DialogTitle, Textarea, Skeleton } from "../components/ui"
import { useToast } from "../components/toast"
import { Shell } from "../components/shell"
import { Plus, Settings, FolderOpen, LayoutList } from "lucide-react"
import { BOARD_TEMPLATES } from "../lib/templates"

/** Project detail page. Lists boards within a project as cards with card counts and colored strips. */
export default function ProjectPage() {
  const { workspaceId, projectId } = useParams({ from: "/workspaces/$workspaceId/projects/$projectId" })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: session } = authClient.useSession()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [templateId, setTemplateId] = useState("blank")
  const [templateStep, setTemplateStep] = useState(true)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [editName, setEditName] = useState("")
  const [editDescription, setEditDescription] = useState("")

  const { data: project, isLoading } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => fetcher<{ id: string; name: string; description: string | null; workspaceId: string; boards: { id: string; name: string; description: string | null; color: string | null; cards: { id: string }[] }[] }>(`/api/projects/${projectId}`),
    enabled: !!session,
  })

  const create = useMutation({
    mutationFn: (body: { projectId: string; name: string; description?: string; templateId?: string }) =>
      fetcher("/api/boards", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] })
      toast("Board created", "success")
      setOpen(false)
      setName("")
      setDescription("")
      setTemplateId("blank")
      setTemplateStep(true)
    },
    onError: (err: any) => toast(err.message || "Failed to create board", "error"),
  })

  const updateProject = useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      fetcher(`/api/projects/${projectId}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project", projectId] })
      queryClient.invalidateQueries({ queryKey: ["projects", workspaceId] })
      toast("Project updated", "success")
      setSettingsOpen(false)
    },
    onError: (err: any) => toast(err.message || "Failed to update project", "error"),
  })

  const deleteProject = useMutation({
    mutationFn: () => fetcher(`/api/projects/${projectId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", workspaceId] })
      toast("Project deleted", "success")
      navigate({ to: "/workspaces/$workspaceId", params: { workspaceId } })
    },
    onError: (err: any) => toast(err.message || "Failed to delete project", "error"),
  })

  if (!session) {
    navigate({ to: "/login" })
    return null
  }
  if (isLoading || !project) return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-64 space-y-3">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )

  return (
    <Shell
      title={project.name}
      back={{ to: "/workspaces/$workspaceId", label: "Workspace" }}
      actions={
        <>
          <Button size="sm" variant="outline" onClick={() => { setEditName(project.name); setEditDescription(project.description || ""); setSettingsOpen(true) }}>
            <Settings className="mr-1 h-4 w-4" /> Settings
          </Button>
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> New board
          </Button>
        </>
      }
    >
      <main className="mx-auto max-w-6xl p-6">
        <h2 className="mb-4 text-xl font-semibold tracking-tight">Boards</h2>
        {project.boards?.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {project.boards.map((b) => (
              <Link key={b.id} to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId" params={{ workspaceId, projectId, boardId: b.id }} className="block h-full w-full">
                <Card className="group relative flex h-full min-h-[11rem] w-full min-w-[16rem] cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-card p-0 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md">
                  <div
                    className="h-2 w-full shrink-0"
                    style={{ background: b.color || "hsl(var(--primary))" }}
                  />
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-start justify-between gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        <LayoutList className="h-6 w-6" />
                      </div>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        {b.cards?.length ?? 0} card{(b.cards?.length ?? 0) !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="mt-3">
                      <h3 className="text-xl font-semibold tracking-tight transition-colors group-hover:text-primary line-clamp-2">{b.name}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground line-clamp-2">{b.description || "No description"}</p>
                    </div>
                    <div className="mt-auto pt-4 flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <span className="rounded-md bg-primary/10 px-2 py-1 text-primary">Board</span>
                      <span>View columns & cards</span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center shadow-sm">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <FolderOpen className="h-7 w-7 text-muted-foreground" />
            </div>
            <h3 className="mb-1 text-lg font-semibold">No boards yet</h3>
            <p className="mb-4 max-w-sm text-sm text-muted-foreground">Create your first board to start organizing tasks.</p>
            <Button onClick={() => setOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> New board
            </Button>
          </div>
        )}
      </main>

      <Dialog open={open} onClose={() => { setOpen(false); setTemplateStep(true) }}>
        <div className="space-y-4">
          <DialogTitle>{templateStep ? "Choose a template" : "Name your board"}</DialogTitle>

          {templateStep ? (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {BOARD_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplateId(t.id)}
                    className={`flex flex-col rounded-xl border p-3 text-left transition-all hover:border-primary/60 hover:bg-muted/40 ${
                      templateId === t.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-background"
                    }`}
                  >
                    <span className="text-xl">{t.emoji}</span>
                    <span className="mt-1 text-sm font-medium">{t.name}</span>
                    <span className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{t.description}</span>
                  </button>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setOpen(false); setTemplateStep(true) }} type="button">Cancel</Button>
                <Button onClick={() => setTemplateStep(false)}>Next →</Button>
              </div>
            </>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); create.mutate({ projectId, name, description, templateId }) }} className="space-y-4">
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                <span className="text-xl">{BOARD_TEMPLATES.find((t) => t.id === templateId)?.emoji}</span>
                <div>
                  <p className="text-sm font-medium">{BOARD_TEMPLATES.find((t) => t.id === templateId)?.name}</p>
                  <button type="button" onClick={() => setTemplateStep(true)} className="text-xs text-muted-foreground hover:text-foreground">Change template</button>
                </div>
              </div>
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTemplateStep(true)} type="button">← Back</Button>
                <Button type="submit" isLoading={create.isPending}>Create board</Button>
              </div>
            </form>
          )}
        </div>
      </Dialog>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <form onSubmit={(e) => { e.preventDefault(); updateProject.mutate({ name: editName, description: editDescription }) }} className="space-y-4">
          <DialogTitle>Project settings</DialogTitle>
          <div>
            <Label>Name</Label>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} required />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} />
          </div>
          <div className="flex justify-between gap-2">
            <Button variant="destructive" type="button" onClick={() => confirm("Delete this project? This cannot be undone.") && deleteProject.mutate()} isLoading={deleteProject.isPending}>Delete project</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsOpen(false)} type="button">Cancel</Button>
              <Button type="submit" isLoading={updateProject.isPending}>Save</Button>
            </div>
          </div>
        </form>
      </Dialog>
    </Shell>
  )
}
